/**
 * D-020 V2B — the A/D hotkey listener (LT2-014/015/016), with #609's
 * browser-interaction fence.
 *
 * Mounted once per hosting surface (ValueDashboard, GameDetail), gated by the
 * caller on lt2Access && lt2HotkeysEnabled. Semantics (packet §V2B; the
 * spoken source correction governs — NOT the handwritten exhibit):
 *
 *   Vertical team layout (moneyline): A = the visually TOP team's visible
 *     NO action, D = the visually BOTTOM team's visible NO action — focus is
 *     per GAME, and the two keys land on two different team markets, never a
 *     YES/NO pair within one market.
 *   Side-by-side two-sided ladder (spread/total): A = LEFT displayed
 *     column, D = RIGHT.
 *
 * Size = the visible one-click preset; price = EXACTLY the displayed
 * top-of-book action the focused card's resolver reports (the same value a
 * mouse click there would submit); the same submit-time guard applies via
 * `submitIntent` — identical path, identical `lt-` coid namespace.
 *
 * ── The fence (#609) ─────────────────────────────────────────────────────
 * This handler is the OUTERMOST of three fences, not the last one:
 * `evaluateIntentGuard` re-validates identity/availability/price/role against
 * the current book at submit time, and the relay enforces the LT2 grant and
 * the contract ceiling. What lives here is only the browser-interaction
 * question — "was this keystroke plausibly a deliberate order at a card the
 * trader is looking at?" — and it answers it by ownership and visibility, not
 * by guessing at page state.
 *
 * Rejections split two ways, and the line matters:
 *
 *   SILENT (never an order attempt in the first place, so a receipt line
 *   would be noise that trains the trader to ignore the strip): Ctrl, Alt
 *   and Meta chords, Shift on a non-letter key (Shift+S is #1067's chord;
 *   Shift on any other letter is refused out loud, D37), IME composition,
 *   key repeat, an open MODAL, a
 *   backgrounded tab, not armed — and an owned keystroke while NOT
 *   armed-and-focused. No focused card is silent for A/D/W/E, which aim at
 *   one visibly-badged card; Pull All is the exception (#802) and gets a
 *   truthful ack instead, because its name already implies its own target.
 *
 *   TRUTHFUL ACK (armed, deliberate, and refused — a silent drop here would
 *   leave the trader unable to tell "refused" from "sent"): the focused
 *   market is gone, the price row is not visible, the focused card is
 *   EXPANDED and therefore paints no price (#647 moneyline), no displayed
 *   price on that side, the line is not 'active', an earlier submission is
 *   unresolved — and an owned keystroke WHILE armed with a card focused,
 *   which is the one silence that misleads rather than merely declines.
 *
 * A refusal must name the state the trader is actually in, because the ack
 * doubles as the instruction for getting out of it. "Not visible" and
 * "expanded" both produce no usable price row, but their fixes are opposite:
 * scroll to the card, versus collapse the card sitting in plain sight. See
 * `expandedSuppressesPrice`.
 *
 * Only the "market is gone" case CLEARS the focus — the card no longer
 * exists. A merely off-screen card KEEPS its focus, so scrolling back
 * restores it without a re-click; so does an expanded one, so collapsing
 * restores it without a re-click either.
 *
 * Overlay handling is deliberately two-part, because "is this keystroke
 * mine?" and "is the card covered?" are different questions. Ownership walks
 * up from the event target, which catches everything a trader can type into
 * or press — including the interiors of portalled menus and popovers.
 * Modality is the only PAGE-level fact worth a global query: a modal can be
 * open with focus still on <body> (nothing inside it clicked yet), and it
 * physically covers the board. Non-modal popovers occlude nothing and must
 * NOT suppress — the pre-#609 bare `[role="dialog"]` query killed A/D
 * page-wide whenever any ⓘ InfoTooltip (cardKit) or the AlertControlsMenu
 * (aria-modal="false") was open, while missing real modals that carry no
 * role.
 *
 * The focused card is located by the marks its own rows paint —
 * `data-lt2-focused="<cardId>"` and `data-lt2-price` (see `focusedPriceRow`).
 * That coupling is deliberate: it keeps the focus store's signature free of
 * DOM, and it is pinned by SpreadsTotalsTable.lt2Focus.test.tsx so a row
 * refactor fails a test rather than silently disarming the fence. Every
 * unknown resolves to REFUSE — no rows, a row claiming another card, no or
 * multiple price rows, a shadow root we cannot see into, or an engine without
 * `checkVisibility`. "We could not tell" must never read as "it is visible".
 *
 * Q, Z, X are DELIBERATELY not bound (future ideas, out of scope). S is the
 * #724 two-sided press: it exists only for a trader who opted in, and it posts
 * the card's A and D joins together (see `fireTwoSided`). Shift+S (#1067) is
 * the one modifier chord that is not silent: the same S press with each leg at
 * the trader's configured multiple of the one-click size. It follows S's key
 * when that key is a letter, and is refused, named, until a multiplier is set. SPACEBAR is the V3 focused-game Pull All:
 * opt-in via its own setting, consumed only while armed + aimed, and always
 * an entry point into the exact-game cancel machinery (lib/trading/pullAll)
 * — authoritative order state, fail-closed selection, frozen snapshot,
 * fenced per-order cancels — never a DOM sweep. W and E are the Live Trading V3 ATTACK keys:
 * the bounded aggressive siblings of A and D. W acts on the same slot A
 * posts on (top/left), E on D's slot (bottom/right) — but instead of joining
 * the displayed top-of-book, an attack submits ONE bounded limit at the
 * level ABOVE the displayed best same-side bid (take it when opposing
 * liquidity is displayed there, establish it when not), at the separate
 * attack size, postOnly false. Every fence below applies to W/E exactly as
 * it does to A/D, plus: the trader must hold the separate attack grant
 * (silent otherwise — the keys don't exist for them), and Attack must be
 * armed on top of one-click (truthful refusal otherwise). The relay
 * re-checks grant, ceiling, and depth on every attack order.
 */
import { useEffect } from 'react';
import {
  oneClickCeiling,
  playLocalRefusalTone,
  useOneClickStore,
  type PlacementReport,
} from '@/stores/oneClickStore';
import { useAttackStore } from '@/stores/attackStore';
import { useAuthStore } from '@/stores/authStore';
import { hasConfirmedGrant } from '@/stores/grants';
import { useDisplaySettings } from '@/stores/displaySettings';
import {
  useLt2FocusStore,
  type Lt2CardTargets,
  type Lt2HotkeyTarget,
} from '@/stores/lt2FocusStore';
import {
  executeFocusedGamePull,
  isPullAllPending,
  type PullAllOutcome,
} from '@/lib/trading/pullAll';
import type { LiveOrderIntent } from '@/lib/trading/liveIntent';
import { lt2ClockStamp } from '@/lib/trading/lt2Timing';
import { lineRefusalReason, twoSidedReadiness, type TwoSidedLeg } from '@/lib/trading/twoSidedReadiness';
import { twoSidedMultiplier, twoSidedOptedIn } from '@/lib/trading/twoSidedOptIn';
import { stepKalshiPriceCents } from '@/lib/pricing/subCent';
import {
  LIVE_HOTKEY_REGISTRY,
  TWO_SIDED_LEVELS_DOWN,
  actionForKey,
  type LiveHotkeyGrants,
  hotkeyDisplayLabel,
  hotkeyPairLabel,
  normalizeHotkey,
  shiftTwoSidedChordLabel,
  supportsShiftChord,
} from '@/lib/trading/liveHotkeyRegistry';
import { readOwnedHotkeys } from '@/hooks/useLiveHotkeys';
import { LT2_READOUT_SELECTOR, readoutSpaceLine, showReadoutLine } from '@/lib/trading/lt2Readout';

let _inFlight = false;

/** What the in-flight hotkey press put on the wire, as its store call reports
 *  it: accepted venue ids, and whether any order's response was lost or came
 *  back without an id. */
interface PressPlacements {
  orders: Array<{ orderId: string; ticker: string }>;
  unknown: boolean;
}

/** The hotkey press in flight, if any. Pull All reads it so an order whose
 *  response lands after the pull's snapshot is swept by a second pass instead
 *  of being left resting on a "pulled" game. B3: its key and start time name
 *  it in the refusal a second key gets while it waits. */
let _inFlightPress: {
  settled: Promise<void>;
  placements: PressPlacements;
  keyLabel: string;
  startedAtMs: number;
} | null = null;

/** True while a Pull All is waiting on an in-flight press for its second
 *  pass; a second Spacebar then refuses as pending, like during the pull. */
let _pullWaiting = false;

/**
 * B3 — the refusal a key gets while an earlier hotkey press waits on the
 * venue: which press, and for how long. The store's 5 s deadline turns a
 * stuck press into an unknown result, so this wait is bounded.
 */
function inFlightRefusal(keyLabel: string): string {
  const waiting = _inFlightPress;
  const seconds = waiting == null ? 0 : (performance.now() - waiting.startedAtMs) / 1000;
  return `${keyLabel} not sent — ${waiting?.keyLabel ?? 'the previous press'} still waiting on the venue (${seconds.toFixed(1)}s)`;
}

function trackInFlight(keyLabel: string, fire: (report: PlacementReport) => Promise<void>): void {
  const placements: PressPlacements = { orders: [], unknown: false };
  const report: PlacementReport = {
    placed: (id, ticker) => {
      if (id == null) placements.unknown = true;
      else placements.orders.push({ orderId: id, ticker });
    },
    lost: () => {
      placements.unknown = true;
    },
  };
  _inFlight = true;
  const startedAtMs = performance.now();
  // Released in the same tick as before this was tracked: `finally` alone.
  const settled: Promise<void> = fire(report).finally(() => {
    _inFlight = false;
    if (_inFlightPress?.settled === settled) _inFlightPress = null;
  });
  _inFlightPress = { settled, placements, keyLabel, startedAtMs };
}

/** D2 — what the strip says about a pull. 'partial' is amber with the
 *  order-rejected descent: something the trader expects gone may still rest. */
interface PullAck {
  status: 'accepted' | 'partial' | 'rejected';
  label: string;
  message: string;
  /** The relay's code on a refused authorization, for the store to act on. */
  code?: string;
}

/** D2 — one pull pass as a strip line. A completed pull names its game and
 *  its scope ("Pull All DAL @ GSV (Kalshi)") and turns partial when a cancel
 *  failed, the refresh or audit was degraded, or Polymarket orders on the
 *  game are still resting. */
function pullAckOf(o: PullAllOutcome): PullAck {
  if (o.status === 'refused') {
    return { status: 'rejected', label: 'Pull All', message: o.message, ...(o.code != null ? { code: o.code } : {}) };
  }
  const partial = o.failed > 0 || o.degraded.length > 0 || o.polyResting > 0 || o.usExcluded === true;
  return {
    status: partial ? 'partial' : 'accepted',
    label: o.status === 'empty' ? 'Pull All' : `Pull All ${o.gameLabel} (Kalshi)`,
    message: o.message,
  };
}

const PULL_ACK_RANK: Record<PullAck['status'], number> = { accepted: 0, partial: 1, rejected: 2 };

/**
 * Pull All's snapshot is the authoritative open-order state AT THE PRESS. A
 * hotkey order still waiting for its venue response is not in it yet (an S
 * press especially: the relay serializes its legs, so the second lands
 * late). The pull therefore runs at once for everything known, then, when
 * the in-flight press settles, once more for the same game, and reports both.
 */
async function pullIncludingInFlight(
  game: Parameters<typeof executeFocusedGamePull>[0],
  inFlight: typeof _inFlightPress,
  onWaiting: (first: PullAck) => void,
): Promise<PullAck> {
  const first = pullAckOf(await executeFocusedGamePull(game));
  if (inFlight == null || first.status === 'rejected') return first;
  // The first pass is already done; say so while the in-flight order settles,
  // which can take seconds when its response is slow or lost.
  _pullWaiting = true;
  try {
    onWaiting(first);
    // Settled either way: a failed placement still ends the wait.
    await inFlight.settled.catch(() => {});
  } finally {
    _pullWaiting = false;
  }
  const { orders, unknown } = inFlight.placements;
  // Exactly that press's accepted orders, by the ids and tickers it reported
  // (not yet in open orders: the create response has no status), and only
  // where they belong to this game. Anything placed after the press is not
  // the pull's to sweep.
  const parts = [first.message];
  let status = first.status;
  // The first pass already ran, so nothing that follows can make the whole
  // pull a refusal: at worst it is partial.
  const atLeast = (next: PullAck['status']) => {
    const capped = next === 'rejected' ? 'partial' : next;
    if (PULL_ACK_RANK[capped] > PULL_ACK_RANK[status]) status = capped;
  };
  if (orders.length > 0) {
    try {
      const late = await executeFocusedGamePull(game, { onlyOrders: orders });
      if (late.status !== 'empty' && late.message !== '') {
        parts.push(`the in-flight hotkey order: ${late.message}`);
        atLeast(pullAckOf(late).status);
      }
    } catch (err) {
      // Keep the first pass's result on the strip; name the failed second.
      parts.push(
        `the in-flight hotkey order: ${
          err instanceof Error && err.message ? err.message : 'pull failed — check open orders'
        }`,
      );
      atLeast('partial');
    }
  }
  if (unknown) {
    // An unknown order is not "pulled".
    parts.push("an in-flight hotkey order's result is unknown — check open orders");
    atLeast('partial');
  }
  return { status, label: first.label, message: parts.join('; ') };
}

/**
 * Anything a keystroke can be "owned by": a surface that CONSUMES letter keys.
 * Matched with `closest()`, so a key pressed on a menu item inside a portalled
 * menu is owned by that menu even though the menu is nowhere near the card in
 * the DOM.
 *
 * Bare buttons (B4, approved Oct 7): a letter does nothing on a <button>, so
 * a bound LETTER or digit pressed on a focused bare button goes to the aimed
 * card — a toolbar click must not eat the trader's next A. Space and Enter
 * stay the button's (they activate it). A button whose popover is open
 * (`aria-expanded="true"`) keeps its keys, and inputs, menus and dialogs stay
 * owners as before. A button inside a `data-lt2-hotkey-control` boundary (the
 * explicit LT2 arm, line, perspective and price controls) hands every key,
 * Space included, back to the focused trading card, as #609 set out.
 *
 * `data-lt2-owner` opts a single button back OUT of that exemption. The
 * boundary is painted on a whole focused row, so it also covers controls that
 * merely live on the row without being LT2's — #602's fee-uncertainty help
 * button sits on the price pill itself, inside the boundary, and a request for
 * an explanation must never hand A/D back to the card. This is the same defect
 * #650 fenced on the resting-order cancel chip: a control that becomes
 * focusable becomes an A/D launch site. The chip could be fixed structurally
 * (it renders in its own row, outside the boundary); a marker drawn ON the
 * price cannot be, so it says so instead.
 *
 * `[role="dialog"]` appears here (interiors) but NOT in MODAL_SELECTOR below
 * (page-level suppression) — a non-modal popover owns its own keystrokes
 * without disabling the board behind it.
 *
 * The scrollable status and target-readout boxes (`[data-lt2-readout]`,
 * #1080) are deliberately NOT owners: a key pressed in one acts on the aimed
 * card as it would from the board. Only Space and Escape are theirs, and
 * `handleLt2Keydown` answers those by name (see `eventReadout`).
 */
const KEY_CONSUMING_OWNER_SELECTOR = [
  'input',
  'textarea',
  'select',
  'dialog',
  '[popover]',
  '[contenteditable]:not([contenteditable="false"])',
  '[role="textbox"]',
  '[role="searchbox"]',
  '[role="combobox"]',
  '[role="listbox"]',
  '[role="option"]',
  '[role="menu"]',
  '[role="menuitem"]',
  '[role="menuitemcheckbox"]',
  '[role="menuitemradio"]',
  '[role="dialog"]',
].join(',');

const LT2_HOTKEY_CONTROL_SELECTOR = '[data-lt2-hotkey-control]';

/** Per-button opt-out from the boundary exemption above (#602). */
const LT2_OWNER_ATTRIBUTE = 'data-lt2-owner';

/**
 * Overlays that COVER the board, and so must suppress hotkeys page-wide even
 * when focus sits on <body>. `data-modal-root` is atoms/Modal's marker — the
 * single modal every LT2-reachable surface renders through; the aria pair is
 * the standards-based equivalent for anything that grows its own.
 */
const MODAL_SELECTOR = '[data-modal-root],[role="dialog"][aria-modal="true"],dialog[open]';

function ownedByInteractive(el: EventTarget | null, key: string): boolean {
  if (!(el instanceof Element)) return false;
  if (el instanceof HTMLElement && el.isContentEditable) return true;
  // A shadow root hides its interior from closest(): `e.target` retargets to
  // the HOST and `activeElement` reports the host, so an <input> inside a
  // password manager's or an editor extension's overlay reads as ordinary page
  // background and its keystrokes would place orders. We cannot see in, so we
  // refuse. A custom-element tag (the dash is the spec's own marker) is treated
  // the same even with a closed root, where `shadowRoot` reads null.
  if (el.shadowRoot != null || el.tagName.includes('-')) return true;
  if (el.closest(KEY_CONSUMING_OWNER_SELECTOR) != null) return true;
  // #1389 — `data-lt2-owner` on a CONTAINER owns every key inside it (a
  // focusable scrolling region, its rows, its toggle), not only on a button:
  // the Injuries report is read with Space and Escape, never traded from.
  if (el.closest(`[${LT2_OWNER_ATTRIBUTE}]`) != null) return true;

  const button = el.closest('button');
  if (button == null) return false;
  if (button.hasAttribute(LT2_OWNER_ATTRIBUTE)) return true;
  if (button.closest(LT2_HOTKEY_CONTROL_SELECTOR) != null) return false;
  // B4 — a bare button owns only the keys it acts on, and every key while its
  // popover is open.
  if (key === ' ' || key === 'Enter') return true;
  return button.getAttribute('aria-expanded') === 'true';
}

/** Interiors of an open popup: Escape there belongs to the popup. */
const POPUP_INTERIOR_SELECTOR =
  'dialog,[popover],[role="dialog"],[role="menu"],[role="listbox"],[role="combobox"]';

/**
 * D21 — the control Escape should hand back to the board: a focused bare
 * button, or a focused input that takes no typed text (checkbox, radio,
 * range…). Never text entry, never a popup's interior, and never a trigger
 * whose popover is open — Escape there closes the popover first (#602).
 */
function escapeBlurTarget(): HTMLElement | null {
  const el = document.activeElement;
  if (!(el instanceof HTMLElement) || el === document.body) return null;
  if (el.closest(POPUP_INTERIOR_SELECTOR) != null) return null;
  if (el.getAttribute('aria-expanded') === 'true') return null;
  if (el instanceof HTMLButtonElement) return el;
  if (el instanceof HTMLInputElement && NON_TEXT_INPUT_TYPES.has(el.type)) return el;
  return null;
}

/** D21 — what the trader would call the focused control, for the refusal:
 *  its label, else "another control". Short, so the strip's line holds it. */
function controlName(e: KeyboardEvent): string {
  for (const origin of eventOrigins(e)) {
    if (!(origin instanceof Element) || origin === document.body) continue;
    const control =
      origin.closest('button,input,select,textarea,[role],[contenteditable]') ?? origin;
    const labelledBy = control.getAttribute('aria-labelledby');
    const candidates = [
      control.getAttribute('aria-label'),
      labelledBy != null ? document.getElementById(labelledBy)?.textContent ?? null : null,
      control instanceof HTMLInputElement ? control.labels?.[0]?.textContent ?? null : null,
      control.getAttribute('title'),
      control instanceof HTMLInputElement ? null : control.textContent,
    ];
    for (const raw of candidates) {
      const name = raw?.replace(/\s+/g, ' ').trim();
      if (name) return name.length > 40 ? `${name.slice(0, 39)}…` : name;
    }
  }
  return 'another control';
}

/**
 * Controls whose keystrokes are TYPING (#967). A subset of the owners above:
 * a bound character pressed here is text, never an order gesture, so it must
 * not even refuse. Inputs that take no typed text (checkbox, radio, range,
 * button-likes) are left out; a key pressed on them with a card aimed still
 * earns the "focus is on another control" refusal.
 */
const TEXT_ENTRY_SELECTOR = [
  'textarea',
  'select',
  '[contenteditable]:not([contenteditable="false"])',
  '[role="textbox"]',
  '[role="searchbox"]',
  '[role="combobox"]',
].join(',');

const NON_TEXT_INPUT_TYPES = new Set([
  'button', 'checkbox', 'color', 'file', 'hidden', 'image', 'radio', 'range', 'reset', 'submit',
]);

function isTextEntry(el: EventTarget | null): boolean {
  if (!(el instanceof Element)) return false;
  if (el instanceof HTMLElement && el.isContentEditable) return true;
  const input = el.closest('input');
  if (input != null) return !NON_TEXT_INPUT_TYPES.has(input.type);
  return el.closest(TEXT_ENTRY_SELECTOR) != null;
}

/** The event's reachable origins: innermost (open shadow roots), target, focus. */
function eventOrigins(e: KeyboardEvent): (EventTarget | null)[] {
  // composedPath()[0] is the real innermost target for OPEN shadow roots,
  // where `e.target` has already retargeted to the host.
  return [e.composedPath?.()[0] ?? e.target, e.target, document.activeElement];
}

/** Ownership across every reachable view of the event's origin. */
function eventIsOwned(e: KeyboardEvent, key: string): boolean {
  return eventOrigins(e).some((origin) => ownedByInteractive(origin, key));
}

function eventIsTyping(e: KeyboardEvent): boolean {
  return eventOrigins(e).some(isTextEntry);
}

/** The status/readout box the key was pressed in (#1080), if any. */
function eventReadout(e: KeyboardEvent): Element | null {
  for (const el of eventOrigins(e)) {
    const box = el instanceof Element ? el.closest(LT2_READOUT_SELECTOR) : null;
    if (box != null) return box;
  }
  return null;
}

/**
 * The element DISPLAYING the price A/D would submit, or null when we cannot
 * say which one that is.
 *
 * A focused card is one or two rows: the action row always, plus — when the
 * card is expanded — the ladder row beneath it. Which one bears the price
 * differs, and that is the whole point of this lookup: expanded, the resolver
 * reads the ladder's join bid (SpreadsTotalsTable `lt2TargetsFor`), which is
 * painted BELOW the action row and can sit under the fold while the action row
 * is comfortably on screen. Checking the action row there would certify a price
 * the trader cannot see — the exact clause this guard exists to enforce. The
 * nearest-lines panel goes one step further (#953): its row spans a sideways
 * scroller that can hide the selected block, so the marks sit on that block's
 * two best-bid PRICES (the text itself, marked exact) rather than on the row.
 *
 * So the rows carry three marks: `data-lt2-focused="<cardId>"` says "part of
 * the focused card", `data-lt2-price` says "this element shows the
 * submittable price", and `data-lt2-price-suppressed` says "this row is
 * deliberately unpriced because it is expanded" (read only by
 * `expandedSuppressesPrice`, to pick the ack wording — never to permit a fire).
 * Every unknown refuses: no rows, a row claiming a different card (stale paint
 * or two cards claiming focus — ambiguous focus is a documented pilot stop
 * condition), or any price-mark shape other than the two below.
 */
function focusedPriceRow(cardId: string, key: 'a' | 'd'): Element | null {
  const rows = [...document.querySelectorAll('[data-lt2-focused]')];
  if (rows.length === 0) return null;
  if (rows.some((r) => r.getAttribute('data-lt2-focused') !== cardId)) return null;
  const priced = rows.filter((r) => r.hasAttribute('data-lt2-price'));
  // One priced row serves BOTH keys in the single two-sided ladder, where
  // one row displays the whole two-action pair.
  if (priced.length === 1) {
    const slot = priced[0].getAttribute('data-lt2-price');
    // A surviving keyed mark can prove only its own side. Missing sibling
    // paint must never let A's visible cents certify D (or the S pair).
    return slot === '' || slot === key ? priced[0] : null;
  }
  // Two priced rows are the game-level moneyline shape: the top/away row is
  // marked "a" and the bottom/home row "d", and the pressed key must prove
  // ITS OWN team's row — A cannot certify the top team's price by finding
  // the bottom team's row on screen. The nearest-lines panel uses the same
  // shape (#953): the selected block's left best-bid price "a", right "d".
  // Collapsed spread/total pills also mark each exact price independently.
  // Anything else is ambiguous and refuses.
  if (priced.length === 2) {
    const keyed = priced.filter((r) => r.getAttribute('data-lt2-price') === key);
    return keyed.length === 1 ? keyed[0] : null;
  }
  return null;
}

/** The card exists and is painted, but no single row shows a submittable
 *  price. Two different states produce that, and the trader needs to be told
 *  which one — the ack is the only instruction they get. */
const ACK_NOT_VISIBLE = "Focused card isn't visible — scroll to it or refocus";
const ACK_EXPANDED = 'Card is expanded — collapse it, then retry';

/**
 * #802 — the armed, opted-in Pull All press with NOTHING aimed.
 *
 * Named by the gesture the trader has to make, not by the state they are in,
 * because on this surface the state is invisible: an unaimed board looks
 * exactly like an aimed one until you know to look for the badge. The chip is
 * a bare ⌨ glyph (its wordy form blew the moneyline density budget, see
 * Lt2FocusBadge), so the ack names the glyph the trader must actually hunt
 * for.
 */
const ACK_NEEDS_AIM = "No game aimed — click a game's ⌨ chip, then retry";

/**
 * A surface may deliberately suppress its own `data-lt2-price` mark while a
 * card is expanded, when the expanded presentation has no truthful two-action
 * A/D reading. The refusal is correct and the focus is kept; this helper only
 * fixes the wording, since such a card is sitting in front of the trader
 * rather than off-screen. (No current surface paints the mark — moneyline
 * keeps its price line visible through expansion, and the spread/total ladder
 * carries a real price row — but the fence stays: any future suppressing
 * surface gets the honest ack, never a fire.)
 *
 * The suppressing row must itself be ON SCREEN to earn the "collapse it" ack:
 * a card that is both expanded and scrolled away is genuinely not visible, and
 * telling the trader to collapse something they cannot see is the same defect
 * in the other direction. Ambiguity refuses to the default the way
 * `focusedPriceRow` does — this only ever changes which true sentence we say.
 */
function expandedSuppressesPrice(cardId: string): boolean {
  const rows = [...document.querySelectorAll('[data-lt2-focused]')];
  if (rows.length === 0) return false;
  if (rows.some((r) => r.getAttribute('data-lt2-focused') !== cardId)) return false;
  const suppressed = rows.filter((r) => r.hasAttribute('data-lt2-price-suppressed'));
  return suppressed.length === 1 && cardIsVisible(suppressed[0]);
}

/**
 * #953 — how much of a ROW-shaped price mark must actually show. "Most" in
 * the ticket's words, read as MORE than half the element's area. Not "any
 * pixel": the pre-#953 overlap test passed a nearest-lines block 17% on
 * screen with every price in it cut off by the strip's own scroller.
 * Measured on #959's review: aimed rows stay at 65–80% shown at 1470 and 1280
 * with the board wider than its frame, so this never refuses a visible row.
 */
const MIN_VISIBLE_FRACTION = 0.5;

/**
 * #953 review — the rounding slack an EXACT mark (`data-lt2-exact`) may lose
 * and still count as whole: layout lands on fractional pixels, so a price
 * sitting flush against a scroller's edge can measure 0.5px short.
 */
const EXACT_TOLERANCE_PX = 1;

/**
 * Rendered, not hidden by CSS, and showing — inside the viewport AND inside
 * every ancestor that clips it. Exported for tests.
 *
 * #953: the viewport alone is not the question. A price inside a sideways
 * scroller (the nearest-lines strip) can sit wholly inside the window while
 * its scroller shows none of it. So the element's rect is cut down by every
 * ancestor whose overflow is not `visible`, per axis — by that ancestor's
 * SCROLLPORT (`clientLeft`/`clientWidth`), so a price under a scrollbar does
 * not count — then by the viewport.
 *
 * Two shapes of mark, two bars. A row-shaped mark (the whole moneyline team
 * row or a Game Detail band) passes at
 * more than half its area. An EXACT mark — `data-lt2-exact`, which the
 * nearest-lines panel and collapsed ST pills put on price TEXT — must be wholly
 * visible: a price cell can be 55% on screen showing only its odds, with the
 * digits the key would post cut off (#959 review).
 *
 * Every ancestor that clips is counted, including `html`/`body`/`#root`
 * (which clip to the window here, so they cut nothing extra). Counting a box
 * that does not really clip can only make this refuse more — never fire more.
 *
 * Shared by every hotkey surface and by `expandedSuppressesPrice`. Every
 * unknown refuses: no `checkVisibility`, an empty rect, or an element not in
 * the document (detached, or inside a shadow root this walk cannot follow).
 */
export function cardIsVisible(node: Element): boolean {
  // Fail closed on an engine that cannot answer, rather than skipping the
  // check — an unanswerable question is not a yes.
  if (typeof node.checkVisibility !== 'function') return false;
  if (!node.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true })) return false;
  if (node.getRootNode() !== node.ownerDocument) return false;
  const rect = node.getBoundingClientRect();
  const area = rect.width * rect.height;
  if (!(area > 0)) return false;
  let { left, top, right, bottom } = rect;
  for (let el = node.parentElement; el != null; el = el.parentElement) {
    const style = window.getComputedStyle(el);
    const clipsX = style.overflowX !== 'visible';
    const clipsY = style.overflowY !== 'visible';
    if (!clipsX && !clipsY) continue;
    // The scrollport, not the border box: a scrollbar or border hides what
    // sits under it. An ancestor with no scrollport to report (0 wide, e.g.
    // an inline box, where overflow has no effect) falls back to its border
    // box rather than cutting the price to nothing.
    const clip = el.getBoundingClientRect();
    if (clipsX) {
      const portLeft = clip.left + el.clientLeft;
      left = Math.max(left, el.clientWidth > 0 ? portLeft : clip.left);
      right = Math.min(right, el.clientWidth > 0 ? portLeft + el.clientWidth : clip.right);
    }
    if (clipsY) {
      const portTop = clip.top + el.clientTop;
      top = Math.max(top, el.clientHeight > 0 ? portTop : clip.top);
      bottom = Math.min(bottom, el.clientHeight > 0 ? portTop + el.clientHeight : clip.bottom);
    }
  }
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
  left = Math.max(left, 0);
  top = Math.max(top, 0);
  right = Math.min(right, viewportWidth);
  bottom = Math.min(bottom, viewportHeight);
  const shownW = Math.max(0, right - left);
  const shownH = Math.max(0, bottom - top);
  if (shownW <= 0 || shownH <= 0) return false;
  if (node.hasAttribute('data-lt2-exact')) {
    return shownW >= rect.width - EXACT_TOLERANCE_PX && shownH >= rect.height - EXACT_TOLERANCE_PX;
  }
  return (shownW * shownH) / area > MIN_VISIBLE_FRACTION;
}

/** Exported for tests. */
export function handleLt2Keydown(e: KeyboardEvent): void {
  // The parity anchor: everything between this stamp and the local ack is
  // what a trader perceives as "hotkey lag" (see lib/trading/lt2Timing).
  // One stamp, both clocks: the monotonic half measures, the epoch half
  // correlates.
  const inputAt = lt2ClockStamp();
  // #725 — WHICH key fires WHICH action is the trader's setting; what each
  // action then does, and every gate it must clear, is unchanged. `normalize`
  // folds letter case (the #609 Shift fold) and both spellings of the space
  // bar. An unmapped key resolves to null and is ordinary typing.
  const key = normalizeHotkey(e.key);
  // Ownership-gated: a mapping belonging to a previously signed-in trader must
  // never route this keystroke. Falls back to the shipped defaults.
  const hotkeys = readOwnedHotkeys();
  // #725 review 2 — the trader's grants decide which actions exist at all, so
  // they are resolved BEFORE the key is matched. An action the trader has no
  // grant for is not in the binding table, so its key is never consumed here
  // and never reserved in Settings: the two halves of "ungranted actions do
  // not consume their mapped key" now come from the same place.
  const grants: LiveHotkeyGrants = {
    attack: hasConfirmedGrant(useAuthStore.getState().user, 'attackAccess'),
    // #724 — the trader's own opt-in, resolved with the grants for the same
    // reason: an un-opted S is not in the binding table, so it types normally
    // and is free to rebind, exactly like an ungranted W. Owner-bound: an
    // opt-in left on this browser by another trader does not count.
    twoSided: twoSidedOptedIn(
      useDisplaySettings.getState(),
      useAuthStore.getState().user?.id ?? null,
    ),
  };
  const action = actionForKey(hotkeys, key, grants);
  const isAttackKey = action === 'attack-top' || action === 'attack-bottom';
  const isPullKey = action === 'pull-all';
  // ⚠️ The KEY, not the action. Before #725 the spacebar could only ever be
  // Pull All, so "is this the pull action?" and "is this the spacebar?" were
  // the same question and the browser-default handling below keyed on the
  // former. Remapping split them: bind a POST to the spacebar and every
  // guard that asks about the action gets the wrong answer about the browser.
  const isSpaceKey = key === ' ';
  // Escape is NOT remappable: it is the app-wide dismissal contract
  // (useDismissibleLayer) and #648 fixes it as the focus clear. It is matched
  // separately so no remap can take it and none can shadow it.
  if (action == null && key !== 'Escape') return;
  // Mid-composition the keystroke belongs to the IME, including Escape (which
  // cancels the composition). `keyCode === 229` is the pre-isComposing signal
  // some IMEs still send.
  if (e.isComposing || e.keyCode === 229) return;

  const owned = eventIsOwned(e, key);
  // #1080 — a status/readout box is not an owner (its keys act on the aimed
  // card), but Space and Escape pressed in one are the box's own.
  const readout = eventReadout(e);
  const focus = useLt2FocusStore.getState();

  if (key === 'Escape') {
    // Escape belongs to whatever the trader is in. In a status or readout box
    // it only leaves the box (the box blurs itself) and the aim survives, as
    // it does when Escape closes a popover (#602). A second Escape, from the
    // board, clears the aim.
    if (readout != null) return;
    // D21 — on a focused button or checkbox it only hands the keyboard back
    // to the board (so Space pulls again); the aim survives. Text fields and
    // open popups keep Escape for themselves.
    const blur = escapeBlurTarget();
    if (blur != null) {
      blur.blur();
      return;
    }
    if (owned) return;
    // Also forgets an aim the board took away (D19): Escape is the trader's.
    if (focus.focusedCardId != null || focus.lostAim != null) focus.clearFocus();
    return;
  }
  // Escape was the only unmapped key with a job. Everything past here needs a
  // mapped action, so an unbound key stays ordinary typing.
  if (action == null) return;

  const oneClick = useOneClickStore.getState();
  if (!oneClick.armed) {
    // D5 — one-click was turned off FOR the trader (a board change, a market
    // that needs the ticket), and the status line says why. A deliberate
    // bound press now rings the order-rejected tone once, so it is never
    // silent. Nothing is written to the strip and the key keeps its browser
    // default (Space still scrolls). The trader's own 1-Click off carries no
    // reason and stays silent, as does every press that was never an order
    // attempt.
    if (
      oneClick.disarmReason != null &&
      !e.repeat &&
      !e.ctrlKey && !e.metaKey && !e.altKey &&
      (!e.shiftKey || supportsShiftChord(key)) &&
      !owned &&
      !(isSpaceKey && readout != null) &&
      (!isPullKey || useDisplaySettings.getState().lt2PullAllEnabled) &&
      document.querySelector(MODAL_SELECTOR) == null &&
      document.visibilityState === 'visible'
    ) {
      playLocalRefusalTone();
    }
    return;
  }
  // #802 — READ here, DECIDED below. This used to be `if (cardId == null)
  // return`, sitting above every silent guard and above the shared
  // preventDefault — which is exactly why an armed, opted-in Pull All press
  // with nothing aimed fell through to the browser and scrolled the pane while
  // saying nothing. The decision now lives at the consumed point, so a press
  // that was never an order attempt (typing, a modal, a background tab, a held
  // key) still says nothing at all.
  const cardId = focus.focusedCardId;
  // Spacebar consumes only when its own opt-in setting is on; otherwise it
  // stays exactly the browser key it always was (scroll, button activation).
  const pullEnabled = isPullKey && useDisplaySettings.getState().lt2PullAllEnabled;
  if (isPullKey && !pullEnabled) return;
  if (e.repeat) {
    // Held key must not machine-gun. A held SPACE additionally must not start
    // scrolling the board mid-hold after its first press was consumed — true
    // for ANY action bound to it, not just Pull All.
    //
    // ⚠️ "after its first press was consumed" is a CONDITION, not a preamble.
    // #802 moved the no-focus decision below this branch, which briefly made a
    // held space claimed even where the first press is deliberately left to
    // the browser — a post bound to Space with nothing aimed, which scrolled
    // once and then stopped. The repeat must claim exactly what the first
    // press claims: a focused card (any action), or the pull action, which is
    // consumed unaimed precisely so it can answer.
    const wouldConsume =
      (cardId != null || isPullKey) &&
      !e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey &&
      document.querySelector(MODAL_SELECTOR) == null &&
      document.visibilityState === 'visible';
    // In a status/readout box a held space keeps scrolling the box, as its
    // first press did (#1080).
    if (isSpaceKey && !owned && readout == null && wouldConsume) e.preventDefault();
    return;
  }
  // #1067 — exactly ONE modifier chord has a job: Shift (alone) on the key
  // that posts both sides, when that key is a letter (`supportsShiftChord`).
  // It is the same press, made larger. Every other chord — Shift on any other
  // action, Ctrl/Alt/Meta on anything — stays the silent no-op it always was.
  // Repeat is decided above, so a held Shift+S is as silent as a held S.
  const isShiftTwoSided =
    e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey &&
    action === 'two-sided' && supportsShiftChord(key);
  // D37 — Shift on any OTHER letter-bound action still never places (the
  // #609 fold), but an armed, aimed Shift+A is a deliberate press, so it is
  // refused out loud below rather than doing nothing. Shift on a non-letter
  // key stays silent: the browser reports a different character (Shift+2 is
  // "@"), so it is not this key at all, and Shift+Space is the scroll-up.
  const isShiftOther =
    e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey &&
    !isShiftTwoSided && supportsShiftChord(key);
  if (
    (e.shiftKey && !isShiftTwoSided && !isShiftOther) ||
    e.ctrlKey || e.metaKey || e.altKey
  ) {
    return;
  }

  // Attack exists only for attack-entitled traders. That is now enforced one
  // step earlier — `actionForKey` cannot return an attack action without the
  // grant — so for everyone else the key stays exactly what it was before V3:
  // ordinary typing, never a dead-key ack for a capability they do not have.
  // Kept as a second, local assertion because this is the branch that submits
  // an aggressive order, and a grant check is not something to hold in one
  // place only.
  if (isAttackKey && !grants.attack) return;

  const keyLabel =
    isShiftTwoSided || isShiftOther ? `⇧${hotkeyDisplayLabel(key)}` : hotkeyDisplayLabel(key);
  // Armed, focused, deliberate — and still refused. Silence here is the one
  // silence that misleads: the trader is mid-flow and cannot tell a refusal
  // from a sent order. Everywhere else an owned keystroke is simply not an
  // order attempt and says nothing.
  if (owned) {
    // A space typed into an input (or activating a focused button) is
    // ordinary browser behavior, never an order gesture — fully silent.
    // Keyed on the SPACEBAR, not on Pull All: a trader who binds a post to
    // space would otherwise get a spurious refusal every time they typed a
    // space into a search box.
    if (isSpaceKey) return;
    // #967 — the same holds for EVERY key typed into a text-entry control: a
    // trader who binds "0" and then types 0 into the size field is typing, not
    // trading. Silent, unconsumed; the browser handles the character.
    if (eventIsTyping(e)) return;
    // "Click the card, then retry" only makes sense to a trader who HAS a
    // card. With nothing aimed there is no order attempt to explain, so this
    // stays the silence it was before #802 moved the no-focus decision below
    // this branch.
    if (cardId == null) return;
    // D21 — clicking the card is the wrong advice (a row click toggles the
    // aim off; a price click places): name the control and the two ways out.
    oneClick.rejectLocal(
      `Hotkey ${keyLabel}`,
      `Focus is on ${controlName(e)} — click empty board space (not the card) or press Esc, then retry`,
    );
    return;
  }
  if (document.querySelector(MODAL_SELECTOR) != null) return;
  // Nobody is looking at a backgrounded tab, so nothing there is deliberate.
  if (document.visibilityState !== 'visible') return;

  // #1080 — Space pressed in a status/readout box scrolls the box, natively
  // and unclaimed, and never pulls or sends: the trader there may be reading
  // a long receipt. Where the board would have taken this press (the same
  // rule as the unaimed branch below), the trader is told: the order-rejected
  // tone once, and a line in that box naming the way out. The strip's receipt
  // is left as it was. Every other bound key from a box acts on the aimed
  // card below, exactly as from the board.
  if (isSpaceKey && readout != null) {
    if (cardId == null && !isPullKey) return;
    playLocalRefusalTone();
    showReadoutLine(readout, readoutSpaceLine(readout, isPullKey ? 'pull' : 'send', cardId != null));
    return;
  }

  // From here the press is CONSUMED: armed, aimed, un-owned, no modal, visible
  // tab. The browser must not ALSO act on it — even when the action then
  // refuses, because the refusal is the response.
  //
  // This used to live inside the Pull All branch alone, which was correct only
  // while the spacebar was Pull All's exclusive key. A post bound to the
  // spacebar scrolled the board on every truthful refusal, and punctuation
  // keys carry their own browser defaults (`/` opens quick-find in Firefox).
  // Every silent guard has already returned above, so nothing that was
  // deliberately left as ordinary typing is affected.

  // ── The unaimed press (#802) ─────────────────────────────────────────────
  // Deliberately asymmetric, and the asymmetry is the point. A/D/W/E aim at
  // ONE card and announce it: an unaimed board simply has no badge anywhere,
  // and their press stays the silent, UNCLAIMED no-op it has always been —
  // `Lt2HotkeyListener.test.tsx`'s "a press we deliberately ignore is NOT
  // claimed" pins that for a post bound to the spacebar. Pull All is the one
  // action whose name already implies its own target ("the current game"), so
  // it is the one press a trader can reasonably believe they have aimed. It
  // gets an answer instead of a scroll.
  //
  // Keyed on the ACTION, never on the spacebar: bind a post to Space and it
  // must keep falling through here untouched.
  if (cardId == null) {
    // D19 — the aimed card left the board (a date, sport or filter change, a
    // card that unmounted). The trader believes they are aimed, so the first
    // press after that is refused, named, with the descent — once. After it,
    // an unaimed press is the ordinary unaimed press below.
    const lostAimReason = focus.lostAimReason;
    const lostAim = focus.takeLostAim();
    if (lostAim != null) {
      e.preventDefault();
      oneClick.rejectLocal(
        isPullKey ? 'Pull All' : `Hotkey ${keyLabel}`,
        lostAimReason === 'line-changed'
          ? `${lostAim}: line changed as prices arrived — aim the line again`
          : `${lostAim} left the board — aim a game`,
      );
      return;
    }
    if (!isPullKey) return;
    // Claimed — the refusal IS the response, so the browser must not also act
    // on the key. Without this the instruction appears AND the pane pages
    // away from the chip the instruction just told the trader to click.
    e.preventDefault();
    oneClick.rejectLocal('Pull All', ACK_NEEDS_AIM);
    return;
  }

  e.preventDefault();

  if (isShiftOther) {
    const chord = shiftTwoSidedChordLabel(hotkeys, grants);
    const plain = hotkeyDisplayLabel(key);
    oneClick.rejectLocal(
      `Hotkey ${keyLabel}`,
      chord != null
        ? `Shift works only with ${chord.slice(1)} (larger both-sides) — press ${plain} alone`
        : `Shift does nothing with ${plain} — press ${plain} alone`,
    );
    return;
  }

  if (isPullKey) {
    const targets = focus.resolveFocused();
    if (!targets) {
      oneClick.rejectLocal('Pull All', 'Focused market is gone — refocus a card');
      focus.clearFocus();
      return;
    }
    const scope = targets.pullScope;
    if (scope == null) {
      oneClick.rejectLocal('Pull All', 'This card cannot anchor a game-scoped pull');
      return;
    }
    // D15 — the relay already refused the pull with PULL_ALL_DISABLED this
    // session: say so here instead of asking again.
    if (oneClick.deskOff.pullAll) {
      oneClick.rejectLocal('Pull All', 'Spacebar Pull All is turned off on this desk');
      return;
    }
    if (isPullAllPending() || _pullWaiting) {
      oneClick.rejectLocal('Pull All', 'Pull All already pending — wait for the result');
      return;
    }
    // D2 — the pending line names the scope; the store rings the neutral
    // tick for it (requests are leaving). Every later state of this pull
    // follows the same press, so a result that lands after a newer press is
    // added beneath that press's line, never over it (D13).
    const press = oneClick.noteAck(
      'pending',
      'Pull All',
      `cancelling resting Kalshi orders on ${targets.cardLabel}…`,
    );
    void pullIncludingInFlight(scope.game, _inFlightPress, (first) =>
      oneClick.noteAck(
        'pending',
        first.label,
        `${first.message}; waiting for the in-flight hotkey order…`,
        press,
      ),
    ).then(
      (ack) => {
        oneClick.noteAck(ack.status, ack.label, ack.message, press);
        // D15 — PULL_ALL_DISABLED / LT2_NOT_ENABLED from the authorization.
        if (ack.code != null) oneClick.noteCapabilityRefusal(ack.code);
      },
      (err: unknown) => {
        oneClick.noteAck(
          'rejected',
          'Pull All',
          err instanceof Error && err.message ? err.message : 'Pull failed — check open orders',
          press,
        );
      },
    );
    return;
  }

  // Armed, focused, entitled — but Attack itself is disarmed. This is the
  // deliberate W/E press the packet's first acceptance test names: visible
  // blocked/disarmed feedback, never a silent drop and never an order.
  if (isAttackKey && !useAttackStore.getState().attackArmed) {
    oneClick.rejectLocal(
      `Hotkey ${keyLabel}`,
      `Attack is disarmed — arm Attack to use ${hotkeyPairLabel(
        hotkeys,
        'attack-top',
        'attack-bottom',
        grants,
      )}`,
    );
    return;
  }

  const targets = focus.resolveFocused();
  if (!targets) {
    oneClick.rejectLocal(`Hotkey ${keyLabel}`, 'Focused market is gone — refocus a card');
    focus.clearFocus();
    return;
  }

  // ── Two-sided S (#724) ───────────────────────────────────────────────────
  // Branched BY NAME before the slot is resolved: S is not a single-sided
  // order, and its fence must prove BOTH displayed rows the way A and D each
  // prove one. Every refusal below is truthful (armed, aimed, deliberate), and
  // nothing here may guess a side, a price or a size.
  const levelsDown = TWO_SIDED_LEVELS_DOWN[action];
  if (levelsDown !== undefined) {
    // D15 — the relay already refused S with LT2_TWO_SIDED_DISABLED this
    // session: refuse here, once and named, instead of two refused legs.
    if (oneClick.deskOff.twoSided) {
      oneClick.rejectLocal(`Hotkey ${keyLabel}`, 'Post both sides is turned off on this desk');
      return;
    }
    if (isShiftTwoSided) {
      // #1067 — no default multiplier exists. Until this trader sets one,
      // the deliberate armed-and-aimed chord is refused and says how to fix
      // it, rather than sending S at its normal size or doing nothing.
      const multiplier = twoSidedMultiplier(
        useDisplaySettings.getState(),
        useAuthStore.getState().user?.id ?? null,
      );
      if (!multiplier.ok) {
        oneClick.rejectLocal(`Hotkey ${keyLabel}`, `${keyLabel} unavailable — ${multiplier.reason}`);
        return;
      }
      fireTwoSided({
        cardId, targets, focus, oneClick, keyLabel, inputAt, levelsDown,
        sizeMultiplier: multiplier.multiplier,
        ceilingContracts: oneClickCeiling(useAuthStore.getState().user),
      });
      return;
    }
    fireTwoSided({ cardId, targets, focus, oneClick, keyLabel, inputAt, levelsDown });
    return;
  }

  // The card still exists, but the row showing the price must be on screen to
  // be fired at. Focus is KEPT either way — scrolling back or collapsing is
  // the fix, not a re-click.
  // W shares A's slot (top/left), E shares D's (bottom/right): the attack
  // fires on the same book/side whose displayed row the fence must prove.
  // The slot is a property of the ACTION, so a remap cannot move an order to
  // a side other than the one whose displayed price the fence just proved.
  // FAIL-CLOSED, and deliberately not `?? 'a'`. An action with no slot is not
  // a single-sided order — Pull All today, the two-sided S action (#724) next.
  // Defaulting a missing slot to the top side would turn a press meant for
  // BOTH sides into one silent top-side order on a live venue. Nothing here
  // may guess which side the trader meant.
  const actionSlot = LIVE_HOTKEY_REGISTRY[action].slot;
  if (actionSlot == null) return;
  const slot: 'a' | 'd' = actionSlot;
  const priceRow = focusedPriceRow(cardId, slot);
  if (priceRow == null || !cardIsVisible(priceRow)) {
    // A row that exists but is off-screen is never "expanded" for ack
    // purposes — the price it would fire at is the one the trader can't see.
    oneClick.rejectLocal(
      `Hotkey ${keyLabel}`,
      priceRow == null && expandedSuppressesPrice(cardId) ? ACK_EXPANDED : ACK_NOT_VISIBLE,
    );
    return;
  }
  const target: Lt2HotkeyTarget | null = slot === 'a' ? targets.a : targets.d;
  if (!target || target.priceCents == null) {
    oneClick.rejectLocal(`Hotkey ${keyLabel}`, 'No displayed price on that side');
    return;
  }
  if (target.availability !== 'active') {
    oneClick.rejectLocal(
      target.label,
      `${lineRefusalReason(target, target.availability)} — order not sent`,
    );
    return;
  }
  if (_inFlight) {
    oneClick.rejectLocal(`Hotkey ${keyLabel}`, inFlightRefusal(keyLabel));
    return;
  }

  // The durable `__lt2` audit stamp is a property of the ACTION, never of the
  // key: `hotkey-a` keeps meaning "the passive top/left action" for every order
  // ever placed, so remapping cannot rewrite what past records say they were
  // placed by. An action carrying no stamp places no order and must not reach
  // the intent builders below.
  const inputMethod = LIVE_HOTKEY_REGISTRY[action].inputMethod;
  if (inputMethod == null) return;

  if (isAttackKey) {
    // The resolver computed the bounded target from the displayed book (best
    // same-side bid + accepted depth). Null means there is nothing truthful
    // to attack: no displayed bid, a fee-hidden price, or a target outside
    // 1..99 — refuse with the reason, exactly like a missing passive price.
    const attack = target.attack;
    if (attack == null) {
      oneClick.rejectLocal(
        `Hotkey ${keyLabel}`,
        'No attackable level — needs a displayed bid with the level above in range',
      );
      return;
    }
    const labelBase = target.label.replace(/ @ \d+¢$/, '');
    const intent: LiveOrderIntent = {
      ticker: target.book?.marketTicker ?? '',
      side: target.side,
      priceCents: attack.targetPriceCents,
      postOnly: false,
      label: `ATT ${labelBase} @ ${attack.targetPriceCents}¢`,
      inputMethod,
      surface: targets.surface,
      marketKind: targets.marketKind,
      period: targets.period,
      line: targets.line,
      focusedCardId: focus.focusedCardId,
      displayedPriceMs: Date.now(),
      inputAtMs: inputAt.wallMs,
      inputAtClock: inputAt,
      attack: { depth: attack.depth, referencePriceCents: attack.referencePriceCents },
    };
    trackInFlight(keyLabel, (report) => oneClick.submitAttackIntent(intent, target.book, report));
    return;
  }

  const intent: LiveOrderIntent = {
    ticker: target.book?.marketTicker ?? '',
    side: target.side,
    priceCents: target.priceCents,
    postOnly: target.postOnly,
    label: target.label,
    inputMethod,
    surface: targets.surface,
    marketKind: targets.marketKind,
    period: targets.period,
    line: targets.line,
    focusedCardId: focus.focusedCardId,
    displayedPriceMs: Date.now(),
    inputAtMs: inputAt.wallMs,
    inputAtClock: inputAt,
  };
  trackInFlight(keyLabel, (report) => oneClick.submitIntent(intent, target.book, report));
}

/**
 * #724 — the two-sided press, after the shared gates have run.
 *
 * Membership is EXACTLY the card's A and D passive targets — the resolver only
 * ever yields those two, so a three-outcome (soccer) card can never grow a
 * draw leg here. What this proves, in order, and refuses truthfully when it
 * cannot:
 *
 *   1. both price rows are painted and on screen (moneyline: the two team
 *      rows; the nearest-lines panel: the selected block's two best-bid
 *      prices; any other derivative: the one row that shows both actions);
 *   2. the card's targets pass `twoSidedReadiness` — two outcomes, both
 *      prices shown on active lines, both prices bid joins, a real pair, and
 *      bids totalling under 100¢ (the header readout asks the same function);
 *   3. no hotkey order is in flight;
 *   4. no earlier S press still has a side whose outcome is unknown.
 *
 * Both legs are sent POST-ONLY. On moneyline that differs from A and D, which
 * keep the pill's postOnly:false for mouse parity: a two-sided press exists to
 * rest on both books, so a leg that would now cross is refused, not filled.
 *
 * The legs keep their own stamps (hotkey-a / hotkey-d) because that is what
 * each order IS; the store adds the shared group stamp that says they were
 * fired together. Size is the one-click size, once, frozen in the store.
 */
function fireTwoSided(args: {
  cardId: string;
  targets: Lt2CardTargets;
  focus: ReturnType<typeof useLt2FocusStore.getState>;
  oneClick: ReturnType<typeof useOneClickStore.getState>;
  keyLabel: string;
  inputAt: ReturnType<typeof lt2ClockStamp>;
  /** #1024 — 0 for S; 1 or 2 rests each leg that many levels below its bid. */
  levelsDown?: 0 | 1 | 2;
  /** #1067 — Shift+S: each leg at this multiple of the one-click size. The
   *  store applies and checks it; nothing else about the press changes. */
  sizeMultiplier?: number;
  /** The trader's one-click ceiling the multiplied size is held to. */
  ceilingContracts?: number;
}): void {
  const {
    cardId, targets, focus, oneClick, keyLabel, inputAt, levelsDown = 0, sizeMultiplier, ceilingContracts,
  } = args;
  const hotkey = `Hotkey ${keyLabel}`;

  const rowA = focusedPriceRow(cardId, 'a');
  const rowD = focusedPriceRow(cardId, 'd');
  if (rowA == null || rowD == null || !cardIsVisible(rowA) || !cardIsVisible(rowD)) {
    oneClick.rejectLocal(
      hotkey,
      (rowA == null || rowD == null) && expandedSuppressesPrice(cardId)
        ? ACK_EXPANDED
        : ACK_NOT_VISIBLE,
    );
    return;
  }
  // Everything that is a property of the card's targets is decided by the
  // same predicate the header readout uses, so the header can only name S
  // when a press would get past these checks.
  const ready = twoSidedReadiness(targets, keyLabel);
  if (!ready.ok) {
    oneClick.rejectLocal(ready.legLabel ?? hotkey, ready.reason);
    return;
  }
  if (_inFlight) {
    oneClick.rejectLocal(hotkey, inFlightRefusal(keyLabel));
    return;
  }
  // An earlier S press with a side whose outcome is still unknown blocks the
  // next one: resending both legs could double a leg that is already live
  // (#724: never blind-retry an unknown leg). A and D stay available.
  const held = oneClick.twoSidedHoldReason();
  if (held != null) {
    oneClick.rejectLocal(hotkey, held);
    return;
  }

  const stampA = LIVE_HOTKEY_REGISTRY['passive-top'].inputMethod;
  const stampD = LIVE_HOTKEY_REGISTRY['passive-bottom'].inputMethod;
  if (stampA == null || stampD == null) return; // registry invariant; never true
  // Every S leg is POST-ONLY, whatever the pill would submit on a click: the
  // readiness check proved each price is a bid join, so post-only only turns
  // a book that moved into a refusal instead of a crossing fill.
  // #1024 — each leg steps down along its own book's price grid. A whole cent
  // is valid on every Kalshi grid, so it is the step when the grid is unknown.
  // Lower than the bid join stays passive, so the S checks above still hold.
  const below = (l: TwoSidedLeg): number | null => {
    let price: number | null = l.priceCents;
    for (let i = 0; i < levelsDown && price !== null; i++) {
      price = stepKalshiPriceCents(price, -1, l.target.book?.priceRanges, 1);
    }
    return price;
  };
  const priceA = below(ready.a);
  const priceD = below(ready.d);
  if (priceA === null || priceD === null) {
    oneClick.rejectLocal(hotkey, `No price ${levelsDown} level${levelsDown === 1 ? '' : 's'} below the bid on both sides`);
    return;
  }
  const leg = (l: TwoSidedLeg, inputMethod: typeof stampA, priceCents: number): LiveOrderIntent => ({
    ticker: l.ticker,
    side: l.target.side,
    priceCents,
    postOnly: true,
    label: l.target.label,
    inputMethod,
    surface: targets.surface,
    marketKind: targets.marketKind,
    period: targets.period,
    line: targets.line,
    focusedCardId: focus.focusedCardId,
    displayedPriceMs: Date.now(),
    inputAtMs: inputAt.wallMs,
    inputAtClock: inputAt,
  });
  trackInFlight(keyLabel, (report) =>
    oneClick.submitTwoSidedIntent({
      // On a three-way game the pair is NO on both teams, which both pay on a
      // draw; the strip says so on every press, not only in a tooltip.
      label: `${keyLabel} ${targets.cardLabel}${targets.threeWay === true ? ' (both NO)' : ''}`,
      legs: [leg(ready.a, stampA, priceA), leg(ready.d, stampD, priceD)],
      books: [ready.a.target.book, ready.d.target.book],
      report,
      ...(sizeMultiplier != null ? { sizeMultiplier, ceilingContracts } : {}),
    }),
  );
}

export function Lt2HotkeyListener() {
  useEffect(() => {
    // `handleLt2Keydown` MUST stay a stable module-level reference. Both
    // hosting surfaces mount this component, and addEventListener dedupes on
    // (type, listener) — so even if they ever mounted together there is one
    // registration and one submission per press. Rewriting this as an inline
    // arrow looks harmless and would quietly create a double-registration
    // (hence double-submit) hazard. The surfaces are exclusive routes today,
    // which is also why the shared cleanup below cannot strand the survivor.
    window.addEventListener('keydown', handleLt2Keydown);
    // Disarming clears the focus: an unfocused, disarmed page is the only
    // safe idle state, and the ring must never suggest A/D are live.
    const unsubscribe = useOneClickStore.subscribe((state, prev) => {
      if (prev.armed && !state.armed) useLt2FocusStore.getState().clearFocus();
    });
    return () => {
      window.removeEventListener('keydown', handleLt2Keydown);
      unsubscribe();
      useLt2FocusStore.getState().clearFocus();
    };
  }, []);
  return null;
}
